import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Shield, Save, Check, X } from 'lucide-react';
import { PageHeader } from './ui/PageHeader';
import { cn } from '../lib/utils';

const features = [
    'notices.create', 'notices.delete',
    'documents.upload', 'documents.delete',
    'polls.create', 'polls.delete',
    'facilities.manage',
    'bookings.approve', 'bookings.reconcile',
    'procurement.manage',
    'group_buys.create', 'group_buys.approve',
    'tickets.assign', 'tickets.view_all',
    'ledger.view_all', 'ledger.ingest',
    'users.manage',
    'settings.manage',
    'pl_statements.upload', 'pl_statements.view',
    'notifications.send'
];

const PermissionsManager: React.FC = () => {
    const [perms, setPerms] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/admin/permissions').then(res => setPerms(res.data));
    }, []);

    const isAllowed = (role: string, feature: string) => {
        const p = perms.find(p => p.role === role && p.feature === feature);
        if (p) return p.is_allowed;
        // Default fallbacks
        if (role === 'admin') return true;
        return false;
    };

    const toggle = (role: string, feature: string) => {
        const existing = perms.find(p => p.role === role && p.feature === feature);
        if (existing) {
            setPerms(perms.map(p => (p.role === role && p.feature === feature) ? { ...p, is_allowed: !p.is_allowed } : p));
        } else {
            setPerms([...perms, { role, feature, is_allowed: true }]);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put('/admin/permissions', perms);
            alert('Permissions updated');
        } catch (e) { alert('Save failed'); }
        setSaving(false);
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Access Control" subtitle="Configure feature permissions for society roles">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-violet-600 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-violet-700 transition-all flex items-center gap-2"
                >
                    <Save size={18} />
                    {saving ? 'Saving...' : 'Save Matrix'}
                </button>
            </PageHeader>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4 font-bold text-slate-600">Feature Module</th>
                            <th className="px-6 py-4 font-bold text-slate-600 text-center w-32">Admin</th>
                            <th className="px-6 py-4 font-bold text-slate-600 text-center w-32">Support</th>
                            <th className="px-6 py-4 font-bold text-slate-600 text-center w-32">Resident</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {features.map(f => (
                            <tr key={f} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-6 py-4 font-medium text-slate-700 font-mono text-xs">{f}</td>
                                <td className="px-6 py-4 text-center">
                                    <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded flex items-center justify-center mx-auto">
                                        <Check size={14} strokeWidth={3} />
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-center">
                                    <button
                                        onClick={() => toggle('support', f)}
                                        className={cn(
                                            "w-6 h-6 rounded flex items-center justify-center mx-auto transition-colors",
                                            isAllowed('support', f) ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-300 hover:bg-slate-200"
                                        )}
                                    >
                                        <Check size={14} strokeWidth={3} />
                                    </button>
                                </td>
                                <td className="px-6 py-4 text-center">
                                    <button
                                        onClick={() => toggle('resident', f)}
                                        className={cn(
                                            "w-6 h-6 rounded flex items-center justify-center mx-auto transition-colors",
                                            isAllowed('resident', f) ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-300 hover:bg-slate-200"
                                        )}
                                    >
                                        <Check size={14} strokeWidth={3} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PermissionsManager;
